/**
 * Enhanced User Profile Modal Component
 * 
 * Implements progressive disclosure pattern for comprehensive user profile display
 * following the specification from USER_PROFILE_MODAL_ENHANCEMENT_SPECIFICATION.md
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface UserProfileData {
  id: string;
  name: string;
  description?: string;
  demographics?: {
    full_name?: string;
    age?: number | string;
    gender?: string;
    location?: string;
    language?: string;
    occupation?: string;
    education_level?: string;
    relationship_status?: string;
  };
  environment?: {
    id?: number;
    environment_name?: string;
    environment_description?: string;
    environment_details?: Record<string, any>;
    is_current?: boolean;
  };
  environments?: Array<{
    id: number;
    environment_name: string;
    environment_description?: string;
    is_current?: boolean;
    created_at?: string;
  }>;
  goals?: Array<{
    title: string;
    description: string;
    goal_type: string;
    importance_according_user: number;
    strength: number;
  }> | {
    short_term: string;
    long_term: string;
    motivation: string;
  };
  preferences?: Array<{
    id: number;
    preference_type: string;
    preference_value: string;
    strength: number;
    context?: string;
    notes?: string;
  }>;
  skills?: Array<{
    id: number;
    skill_name: string;
    skill_description: string;
    proficiency_level: number;
    practice_frequency?: string;
    last_practiced?: string;
    skill_category: string;
  }>;
  beliefs?: Array<{
    id: number;
    content: string;
    strength: number;
    certainty?: number;
    life_impact?: number;
    emotionality?: number;
    stability?: number;
    user_awareness?: number;
    generic_belief?: {
      name: string;
      description: string;
    };
  }>;
  traits?: Array<{
    id: number;
    strength: number;
    awareness?: number;
    generic_trait?: {
      code: string;
      name: string;
      description: string;
      trait_type: string;
    };
  }>;
  trust_level?: {
    overall_trust: number;
    trust_phase: string;
    domain_scores: Record<string, number>;
    last_updated?: string;
  };
  resources?: Array<{
    id: number;
    resource_name: string;
    resource_description: string;
    availability?: string;
    condition?: string;
    ownership?: string;
    environment_name: string;
    resource_category: string;
  }>;
  statistics?: {
    total_environments: number;
    total_skills: number;
    total_resources: number;
    total_preferences: number;
    total_beliefs: number;
    total_traits: number;
  };
  is_real?: boolean;
}

@customElement('user-profile-modal')
export class UserProfileModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) profileData: UserProfileData | null = null;
  @property({ type: Boolean }) loading = false;
  @state() private expandedSections = new Set<string>(['basic']); // Basic section expanded by default
  @state() private isEditing = false;
  @state() private editedData: any = {};

  static styles = css`
    /* Modal Overlay */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      z-index: 2000; /* Tier 1: Standard modals (mutually exclusive with other modals) */
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
      padding: var(--spacing-4);
    }

    .modal-overlay.open {
      opacity: 1;
      pointer-events: all;
    }

    /* Modal Container */
    .modal {
      background: var(--surface-color, #ffffff);
      border-radius: var(--border-radius-lg, 12px);
      box-shadow: var(--shadow-xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25));
      max-width: 600px;
      width: 100%;
      max-height: 90vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transform: scale(0.95);
      transition: transform 0.3s ease;
    }

    .modal-overlay.open .modal {
      transform: scale(1);
    }

    /* Modal Header */
    .modal-header {
      padding: var(--spacing-6, 24px);
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: var(--surface-color, #ffffff);
    }

    .modal-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--text-primary, #111827);
      margin: 0;
      display: flex;
      align-items: center;
      gap: var(--spacing-2, 8px);
    }

    .modal-close {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: var(--text-secondary, #6b7280);
      padding: var(--spacing-2, 8px);
      border-radius: var(--border-radius, 6px);
      transition: all 0.2s ease;
    }

    .modal-close:hover {
      background: var(--surface-hover, #f3f4f6);
      color: var(--text-primary, #111827);
    }

    /* Modal Body */
    .modal-body {
      flex: 1;
      overflow-y: auto;
      padding: 0;
    }

    /* Loading State */
    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-12, 48px);
      color: var(--text-secondary, #6b7280);
    }

    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--border-color, #e5e7eb);
      border-top: 3px solid var(--primary-color, #3b82f6);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-4, 16px);
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    /* Profile Sections */
    .profile-section {
      border-bottom: 1px solid var(--border-color, #e5e7eb);
    }

    .profile-section:last-child {
      border-bottom: none;
    }

    .profile-section-header {
      padding: var(--spacing-4, 16px) var(--spacing-6, 24px);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: var(--surface-color, #ffffff);
      transition: background-color 0.2s ease;
    }

    .profile-section-header:hover {
      background: var(--surface-hover, #f9fafb);
    }

    .profile-section-header h4 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--text-primary, #111827);
      display: flex;
      align-items: center;
      gap: var(--spacing-2, 8px);
    }

    .profile-section-icon {
      font-size: 1.2rem;
    }

    .profile-section-toggle {
      font-size: 0.875rem;
      color: var(--text-secondary, #6b7280);
      transition: transform 0.2s ease;
    }

    .profile-section.expanded .profile-section-toggle {
      transform: rotate(180deg);
    }

    .profile-section-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease;
    }

    .profile-section.expanded .profile-section-content {
      max-height: 1000px; /* Large enough to accommodate content */
    }

    .profile-section-body {
      padding: 0 var(--spacing-6, 24px) var(--spacing-4, 16px);
    }

    /* Compact Basic Section (Always Visible) */
    .profile-compact-section {
      padding: var(--spacing-6, 24px);
      background: var(--surface-color, #ffffff);
    }

    .profile-compact-header h4 {
      margin: 0 0 var(--spacing-4, 16px) 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--text-primary, #111827);
      display: flex;
      align-items: center;
      gap: var(--spacing-2, 8px);
    }

    .profile-compact-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: var(--spacing-4, 16px);
    }

    .profile-field-compact {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-1, 4px);
    }

    .profile-field-compact.profile-field-full-width {
      grid-column: 1 / -1;
    }

    .profile-field-compact label {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--text-secondary, #6b7280);
    }

    .profile-value-compact {
      font-size: 0.875rem;
      color: var(--text-primary, #111827);
      padding: var(--spacing-2, 8px);
      background: var(--surface-secondary, #f9fafb);
      border-radius: var(--border-radius, 6px);
      border: 1px solid var(--border-color, #e5e7eb);
    }

    /* Profile Fields */
    .profile-field {
      margin-bottom: var(--spacing-4, 16px);
    }

    .profile-field:last-child {
      margin-bottom: 0;
    }

    .profile-field label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--text-secondary, #6b7280);
      margin-bottom: var(--spacing-2, 8px);
    }

    .profile-value {
      font-size: 0.875rem;
      color: var(--text-primary, #111827);
      line-height: 1.5;
    }

    /* Goal-specific styling */
    .goal-description {
      margin-bottom: var(--spacing-2, 8px);
    }

    .goal-meta {
      display: flex;
      gap: var(--spacing-4, 16px);
      font-size: 0.75rem;
      color: var(--text-secondary, #6b7280);
    }

    /* Environment details */
    .environment-details {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-2, 8px);
    }

    .detail-item {
      display: flex;
      justify-content: space-between;
      padding: var(--spacing-2, 8px);
      background: var(--surface-secondary, #f9fafb);
      border-radius: var(--border-radius, 6px);
    }

    .detail-key {
      font-weight: 500;
      color: var(--text-secondary, #6b7280);
    }

    .detail-value {
      color: var(--text-primary, #111827);
    }

    /* Modal Footer */
    .modal-footer {
      padding: var(--spacing-6, 24px);
      border-top: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      justify-content: space-between;
      gap: var(--spacing-3, 12px);
      background: var(--surface-color, #ffffff);
    }

    .btn {
      padding: var(--spacing-2, 8px) var(--spacing-4, 16px);
      border-radius: var(--border-radius, 6px);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid transparent;
    }

    .btn-secondary {
      background: var(--surface-secondary, #f9fafb);
      color: var(--text-secondary, #6b7280);
      border-color: var(--border-color, #e5e7eb);
    }

    .btn-secondary:hover {
      background: var(--surface-hover, #f3f4f6);
      color: var(--text-primary, #111827);
    }

    .btn-primary {
      background: var(--primary-color, #3b82f6);
      color: white;
    }

    .btn-primary:hover {
      background: var(--primary-hover, #2563eb);
    }

    /* Mobile Responsive */
    @media (max-width: 768px) {
      .modal-overlay {
        padding: var(--spacing-2, 8px);
      }

      .modal {
        max-height: 95vh;
      }

      .profile-compact-grid {
        grid-template-columns: 1fr;
      }

      .modal-footer {
        flex-direction: column;
      }
    }

    /* Skills, Traits, Beliefs, Resources Styling */
    .skill-meta, .trait-meta, .belief-meta, .resource-meta {
      margin-top: var(--spacing-2, 8px);
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-2, 8px);
      font-size: 0.75rem;
      color: var(--text-secondary, #6b7280);
    }

    .skill-meta span, .trait-meta span, .belief-meta span, .resource-meta span {
      background: var(--surface-secondary, #f9fafb);
      padding: var(--spacing-1, 4px) var(--spacing-2, 8px);
      border-radius: var(--border-radius, 6px);
      border: 1px solid var(--border-color, #e5e7eb);
    }

    .skill-description, .trait-description, .belief-content, .resource-description {
      margin-bottom: var(--spacing-2, 8px);
      font-size: 0.875rem;
      line-height: 1.4;
    }

    /* Trust Level Styling */
    .trust-level {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--primary-color, #3b82f6);
      margin-bottom: var(--spacing-1, 4px);
    }

    .trust-phase {
      font-size: 0.875rem;
      color: var(--text-secondary, #6b7280);
      margin-bottom: var(--spacing-2, 8px);
    }

    .trust-domains {
      margin-top: var(--spacing-2, 8px);
    }

    .domain-score {
      display: flex;
      justify-content: space-between;
      padding: var(--spacing-1, 4px) 0;
      border-bottom: 1px solid var(--border-color, #e5e7eb);
    }

    .domain-score:last-child {
      border-bottom: none;
    }

    .domain-name {
      font-weight: 500;
    }

    .domain-value {
      color: var(--primary-color, #3b82f6);
      font-weight: 600;
    }

    .trust-updated {
      margin-top: var(--spacing-2, 8px);
      font-size: 0.75rem;
      color: var(--text-secondary, #6b7280);
      font-style: italic;
    }

    /* Editing Styles */
    .editing-indicator {
      font-size: 0.75rem;
      color: var(--primary-color, #3b82f6);
      font-weight: 500;
      margin-left: var(--spacing-2, 8px);
    }

    .editing-notice {
      background: var(--info-bg, #eff6ff);
      border: 1px solid var(--info-border, #bfdbfe);
      border-radius: var(--border-radius, 6px);
      padding: var(--spacing-3, 12px);
      margin-bottom: var(--spacing-4, 16px);
    }

    .editing-notice p {
      margin: 0;
      font-size: 0.875rem;
      color: var(--info-text, #1e40af);
    }

    .profile-input-compact {
      width: 100%;
      padding: var(--spacing-2, 8px);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: var(--border-radius, 6px);
      font-size: 0.875rem;
      background: var(--surface-color, #ffffff);
      transition: border-color 0.2s ease;
    }

    .profile-input-compact:focus {
      outline: none;
      border-color: var(--primary-color, #3b82f6);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .text-muted {
      color: var(--text-secondary, #6b7280);
      font-size: 0.75rem;
      font-style: italic;
      margin-top: var(--spacing-1, 4px);
      display: block;
    }
  `;

  /**
   * Toggle section expansion
   */
  private toggleSection = (sectionName: string) => {
    if (this.expandedSections.has(sectionName)) {
      this.expandedSections.delete(sectionName);
    } else {
      this.expandedSections.add(sectionName);
    }
    this.requestUpdate();
  };

  /**
   * Close modal
   */
  private closeModal = () => {
    this.dispatchEvent(new CustomEvent('close-modal', { bubbles: true }));
  };

  /**
   * Start editing mode
   */
  private startEditing = () => {
    this.isEditing = true;
    // Initialize edited data with current demographics
    this.editedData = {
      demographics: { ...this.profileData?.demographics }
    };
  };

  /**
   * Cancel editing mode
   */
  private cancelEditing = () => {
    this.isEditing = false;
    this.editedData = {};
  };

  /**
   * Save edited data
   */
  private saveEditing = async () => {
    try {
      // Call API to save demographics changes
      const response = await fetch('/api/user/profile/demographics/', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCsrfToken()
        },
        body: JSON.stringify(this.editedData.demographics)
      });

      if (response.ok) {
        // Update profile data with saved changes
        if (this.profileData) {
          this.profileData = {
            ...this.profileData,
            demographics: { ...this.editedData.demographics }
          };
        }
        this.isEditing = false;
        this.editedData = {};

        // Show success message
        this.dispatchEvent(new CustomEvent('profile-updated', {
          bubbles: true,
          detail: { message: 'Demographics updated successfully!' }
        }));
      } else {
        throw new Error('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      // Show error message
      this.dispatchEvent(new CustomEvent('profile-error', {
        bubbles: true,
        detail: { message: 'Failed to save changes. Please try again.' }
      }));
    }
  };

  /**
   * Get CSRF token for API requests
   */
  private getCsrfToken(): string {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    return token || '';
  };

  /**
   * Handle input changes during editing
   */
  private handleInputChange = (field: string, value: string) => {
    this.editedData = {
      ...this.editedData,
      demographics: {
        ...this.editedData.demographics,
        [field]: value
      }
    };
  };

  render() {
    if (!this.open) return html``;

    return html`
      <div class="modal-overlay ${this.open ? 'open' : ''}" @click=${this.closeModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">
              👤 User Profile
            </h3>
            <button class="modal-close" data-testid="profile-modal-close-btn" @click=${this.closeModal}>×</button>
          </div>

          <div class="modal-body">
            ${this.loading ? html`
              <div class="loading-state">
                <div class="spinner"></div>
                <p>Loading profile...</p>
              </div>
            ` : this.profileData ? this.renderProfileContent() : html`
              <div class="loading-state">
                <p>No profile data available</p>
              </div>
            `}
          </div>

          <div class="modal-footer">
            ${this.isEditing ? html`
              <button class="btn btn-secondary" data-testid="profile-cancel-btn" @click=${this.cancelEditing}>
                Cancel
              </button>
              <button class="btn btn-primary" data-testid="profile-save-btn" @click=${this.saveEditing}>
                Save Changes
              </button>
            ` : html`
              <button class="btn btn-secondary" @click=${this.closeModal}>
                Close
              </button>
              <button class="btn btn-primary" data-testid="profile-edit-btn" @click=${this.startEditing}>
                Edit Profile
              </button>
            `}
          </div>
        </div>
      </div>
    `;
  }

  private renderProfileContent() {
    if (!this.profileData) return '';

    return html`
      <!-- Basic Information & Demographics - Always Visible -->
      ${this.renderBasicSection()}

      <!-- Goals & Aspirations - Collapsible -->
      ${this.renderGoalsSection()}

      <!-- Environment & Context - Collapsible -->
      ${this.renderEnvironmentSection()}

      <!-- Skills & Abilities - Collapsible -->
      ${this.renderSkillsSection()}

      <!-- Personality Traits - Collapsible -->
      ${this.renderTraitsSection()}

      <!-- Beliefs & Values - Collapsible -->
      ${this.renderBeliefsSection()}

      <!-- Preferences - Collapsible -->
      ${this.renderPreferencesSection()}
    `;
  }

  private renderBasicSection() {
    const profile = this.profileData!;
    const editData = this.editedData.demographics || {};

    return html`
      <div class="profile-compact-section">
        <div class="profile-compact-header">
          <h4>
            <span class="profile-section-icon">👤</span>
            Basic Information & Demographics
            ${this.isEditing ? html`<span class="editing-indicator">✏️ Editing</span>` : ''}
          </h4>
        </div>

        ${this.isEditing ? html`
          <div class="editing-notice">
            <p><strong>📝 Limited Editing:</strong> You can currently edit basic demographics here.
            Goals, skills, and other profile sections will be editable by users themselves soon!</p>
          </div>
        ` : ''}

        <div class="profile-compact-grid">
          <div class="profile-field-compact">
            <label>Full Name</label>
            ${this.isEditing ? html`
              <input
                type="text"
                class="profile-input-compact"
                data-testid="profile-input-full-name"
                .value=${editData.full_name || profile.demographics?.full_name || ''}
                @input=${(e: any) => this.handleInputChange('full_name', e.target.value)}
              />
            ` : html`
              <div class="profile-value-compact" data-testid="profile-value-full-name">
                ${profile.demographics?.full_name || profile.name || 'Not set'}
              </div>
            `}
          </div>
          <div class="profile-field-compact">
            <label>Age</label>
            ${this.isEditing ? html`
              <input
                type="number"
                class="profile-input-compact"
                data-testid="profile-input-age"
                .value=${editData.age || profile.demographics?.age || ''}
                @input=${(e: any) => this.handleInputChange('age', e.target.value || 0)}
              />
            ` : html`
              <div class="profile-value-compact" data-testid="profile-value-age">
                ${profile.demographics?.age || 'Not set'}
              </div>
            `}
          </div>
          <div class="profile-field-compact">
            <label>Gender</label>
            ${this.isEditing ? html`
              <input
                type="text"
                class="profile-input-compact"
                data-testid="profile-input-gender"
                .value=${editData.gender || profile.demographics?.gender || ''}
                @input=${(e: any) => this.handleInputChange('gender', e.target.value)}
              />
            ` : html`
              <div class="profile-value-compact" data-testid="profile-value-gender">
                ${profile.demographics?.gender || 'Not set'}
              </div>
            `}
          </div>
          <div class="profile-field-compact">
            <label>Location</label>
            ${this.isEditing ? html`
              <input
                type="text"
                class="profile-input-compact"
                data-testid="profile-input-location"
                .value=${editData.location || profile.demographics?.location || ''}
                @input=${(e: any) => this.handleInputChange('location', e.target.value)}
              />
            ` : html`
              <div class="profile-value-compact" data-testid="profile-value-location">
                ${profile.demographics?.location || 'Not set'}
              </div>
            `}
          </div>
          <div class="profile-field-compact">
            <label>Language</label>
            ${this.isEditing ? html`
              <input
                type="text"
                class="profile-input-compact"
                data-testid="profile-input-language"
                .value=${editData.language || profile.demographics?.language || ''}
                @input=${(e: any) => this.handleInputChange('language', e.target.value)}
              />
            ` : html`
              <div class="profile-value-compact" data-testid="profile-value-language">
                ${profile.demographics?.language || 'Not set'}
              </div>
            `}
          </div>
          <div class="profile-field-compact">
            <label>Occupation</label>
            ${this.isEditing ? html`
              <input
                type="text"
                class="profile-input-compact"
                data-testid="profile-input-occupation"
                .value=${editData.occupation || profile.demographics?.occupation || ''}
                @input=${(e: any) => this.handleInputChange('occupation', e.target.value)}
              />
            ` : html`
              <div class="profile-value-compact" data-testid="profile-value-occupation">
                ${profile.demographics?.occupation || 'Not set'}
              </div>
            `}
          </div>
          <div class="profile-field-compact profile-field-full-width">
            <label>Profile Description</label>
            <div class="profile-value-compact">
              ${profile.description || 'Not set'}
              ${this.isEditing ? html`<small class="text-muted">(Profile description editing coming soon)</small>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderGoalsSection() {
    const profile = this.profileData!;
    const isExpanded = this.expandedSections.has('goals');

    return html`
      <div class="profile-section ${isExpanded ? 'expanded' : ''}">
        <div class="profile-section-header" data-testid="profile-section-goals" @click=${() => this.toggleSection('goals')}>
          <h4>
            <span class="profile-section-icon">🎯</span>
            Goals & Aspirations
          </h4>
          <span class="profile-section-toggle">▼</span>
        </div>
        <div class="profile-section-content">
          <div class="profile-section-body">
            ${profile.goals && Array.isArray(profile.goals) && profile.goals.length > 0 ?
              profile.goals.map((goal) => html`
                <div class="profile-field">
                  <label>${goal.title} (${goal.goal_type})</label>
                  <div class="profile-value">
                    <div class="goal-description">${goal.description}</div>
                    <div class="goal-meta">
                      <span class="goal-importance">Importance: ${goal.importance_according_user}/100</span>
                      <span class="goal-strength">Strength: ${goal.strength}/100</span>
                    </div>
                  </div>
                </div>
              `) : html`
                <div class="profile-field">
                  <label>Status</label>
                  <div class="profile-value">No goals defined yet</div>
                </div>
              `
            }
          </div>
        </div>
      </div>
    `;
  }

  private renderEnvironmentSection() {
    const profile = this.profileData!;
    const isExpanded = this.expandedSections.has('environment');

    return html`
      <div class="profile-section ${isExpanded ? 'expanded' : ''}">
        <div class="profile-section-header" data-testid="profile-section-environment" @click=${() => this.toggleSection('environment')}>
          <h4>
            <span class="profile-section-icon">🏠</span>
            Environment & Context
          </h4>
          <span class="profile-section-toggle">▼</span>
        </div>
        <div class="profile-section-content">
          <div class="profile-section-body">
            ${profile.environment ? html`
              <div class="profile-field">
                <label>Current Environment</label>
                <div class="profile-value">${profile.environment.environment_name || 'Not set'}</div>
              </div>
              <div class="profile-field">
                <label>Environment Description</label>
                <div class="profile-value">${profile.environment.environment_description || 'Not set'}</div>
              </div>
              ${profile.environment.environment_details ? html`
                <div class="profile-field">
                  <label>Environment Details</label>
                  <div class="profile-value environment-details">
                    ${Object.entries(profile.environment.environment_details).map(([key, value]) => html`
                      <div class="detail-item">
                        <span class="detail-key">${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                        <span class="detail-value">${value}</span>
                      </div>
                    `)}
                  </div>
                </div>
              ` : ''}
            ` : html`
              <div class="profile-field">
                <label>Status</label>
                <div class="profile-value">No environment information available</div>
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  }

  private renderPreferencesSection() {
    const profile = this.profileData!;
    const isExpanded = this.expandedSections.has('preferences');

    return html`
      <div class="profile-section ${isExpanded ? 'expanded' : ''}">
        <div class="profile-section-header" data-testid="profile-section-preferences" @click=${() => this.toggleSection('preferences')}>
          <h4>
            <span class="profile-section-icon">⚙️</span>
            Preferences
          </h4>
          <span class="profile-section-toggle">▼</span>
        </div>
        <div class="profile-section-content">
          <div class="profile-section-body">
            ${profile.preferences && Array.isArray(profile.preferences) && profile.preferences.length > 0 ?
              profile.preferences.map((pref) => html`
                <div class="profile-field">
                  <label>${pref.preference_type}</label>
                  <div class="profile-value">
                    <div>${pref.preference_value}</div>
                    <div class="goal-meta">
                      <span>Strength: ${pref.strength}/100</span>
                      ${pref.context ? html`<span>Context: ${pref.context}</span>` : ''}
                    </div>
                  </div>
                </div>
              `) : html`
                <div class="profile-field">
                  <label>Status</label>
                  <div class="profile-value">No preferences defined yet</div>
                </div>
              `
            }
          </div>
        </div>
      </div>
    `;
  }

  private renderSkillsSection() {
    const profile = this.profileData!;
    const isExpanded = this.expandedSections.has('skills');

    return html`
      <div class="profile-section ${isExpanded ? 'expanded' : ''}">
        <div class="profile-section-header" data-testid="profile-section-skills" @click=${() => this.toggleSection('skills')}>
          <h4>
            <span class="profile-section-icon">🎯</span>
            Skills & Abilities ${profile.statistics ? `(${profile.statistics.total_skills})` : ''}
          </h4>
          <span class="profile-section-toggle">▼</span>
        </div>
        <div class="profile-section-content">
          <div class="profile-section-body">
            ${profile.skills && Array.isArray(profile.skills) && profile.skills.length > 0 ?
              profile.skills.map((skill) => html`
                <div class="profile-field">
                  <label>${skill.skill_name} (${skill.skill_category})</label>
                  <div class="profile-value">
                    <div class="skill-description">${skill.skill_description}</div>
                    <div class="skill-meta">
                      <span class="skill-proficiency">Proficiency: ${skill.proficiency_level}/100</span>
                      ${skill.practice_frequency ? html`<span class="skill-frequency">Practice: ${skill.practice_frequency}</span>` : ''}
                      ${skill.last_practiced ? html`<span class="skill-last">Last practiced: ${new Date(skill.last_practiced).toLocaleDateString()}</span>` : ''}
                    </div>
                  </div>
                </div>
              `) : html`
                <div class="profile-field">
                  <label>Status</label>
                  <div class="profile-value">No skills recorded yet</div>
                </div>
              `
            }
          </div>
        </div>
      </div>
    `;
  }

  private renderTraitsSection() {
    const profile = this.profileData!;
    const isExpanded = this.expandedSections.has('traits');

    return html`
      <div class="profile-section ${isExpanded ? 'expanded' : ''}">
        <div class="profile-section-header" data-testid="profile-section-traits" @click=${() => this.toggleSection('traits')}>
          <h4>
            <span class="profile-section-icon">🧠</span>
            Personality Traits ${profile.statistics ? `(${profile.statistics.total_traits})` : ''}
          </h4>
          <span class="profile-section-toggle">▼</span>
        </div>
        <div class="profile-section-content">
          <div class="profile-section-body">
            ${profile.traits && Array.isArray(profile.traits) && profile.traits.length > 0 ?
              profile.traits.map((trait) => html`
                <div class="profile-field">
                  <label>${trait.generic_trait?.name || 'Unknown Trait'} (${trait.generic_trait?.trait_type || 'PERSONALITY'})</label>
                  <div class="profile-value">
                    <div class="trait-description">${trait.generic_trait?.description || ''}</div>
                    <div class="trait-meta">
                      <span class="trait-strength">Strength: ${trait.strength}/100</span>
                      ${trait.awareness ? html`<span class="trait-awareness">Awareness: ${trait.awareness}/100</span>` : ''}
                    </div>
                  </div>
                </div>
              `) : html`
                <div class="profile-field">
                  <label>Status</label>
                  <div class="profile-value">No personality traits recorded yet</div>
                </div>
              `
            }
          </div>
        </div>
      </div>
    `;
  }

  private renderBeliefsSection() {
    const profile = this.profileData!;
    const isExpanded = this.expandedSections.has('beliefs');

    return html`
      <div class="profile-section ${isExpanded ? 'expanded' : ''}">
        <div class="profile-section-header" data-testid="profile-section-beliefs" @click=${() => this.toggleSection('beliefs')}>
          <h4>
            <span class="profile-section-icon">💭</span>
            Beliefs & Values ${profile.statistics ? `(${profile.statistics.total_beliefs})` : ''}
          </h4>
          <span class="profile-section-toggle">▼</span>
        </div>
        <div class="profile-section-content">
          <div class="profile-section-body">
            ${profile.beliefs && Array.isArray(profile.beliefs) && profile.beliefs.length > 0 ?
              profile.beliefs.map((belief) => html`
                <div class="profile-field">
                  <label>${belief.generic_belief?.name || 'Personal Belief'}</label>
                  <div class="profile-value">
                    <div class="belief-content">${belief.content}</div>
                    <div class="belief-meta">
                      <span class="belief-strength">Strength: ${belief.strength}/100</span>
                      ${belief.certainty ? html`<span class="belief-certainty">Certainty: ${belief.certainty}/100</span>` : ''}
                      ${belief.life_impact ? html`<span class="belief-impact">Life Impact: ${belief.life_impact}/100</span>` : ''}
                    </div>
                  </div>
                </div>
              `) : html`
                <div class="profile-field">
                  <label>Status</label>
                  <div class="profile-value">No beliefs recorded yet</div>
                </div>
              `
            }
          </div>
        </div>
      </div>
    `;
  }


}
